/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ActionTypes,
	confirmEmployeeRegistrationSceneActivated,
	confirmEmployeeRegistrationSceneEnterPasswordValidationException,
	confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeUnexpectedError,
	confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeValidationException,
	confirmEmployeeRegistrationScenePasswordInputChanged,
	confirmEmployeeRegistrationScenePasswordStrengthEvaluated,
	confirmEmployeeRegistrationScenePasswordSubmitted,
	confirmEmployeeRegistrationScenePhoneVerificationCodeInputChanged,
	confirmEmployeeRegistrationScenePhoneVerificationCodeSubmitted,
	confirmEmployeeRegistrationSceneRequestRestartPhoneVerification,
	confirmEmployeeRegistrationSceneRequestRetryPhoneVerification,
	confirmEmployeeRegistrationSceneRestartPhoneVerificationUnexpectedError,
	confirmEmployeeRegistrationSceneRetryPhoneVerificationUnexpectedError,
	confirmEmployeeRegistrationSceneStateChanged,
	confirmEmployeeRegistrationSceneStateRetrieved
} from './actions';
import {getType} from 'typesafe-actions';
import {combineReducers} from 'redux';
import {ConstraintViolationDTO} from '../../model/validation';
import {MessageType} from '../../model/messageType';
import {DisplayType} from './displayType';

export interface EnterPasswordState {
	email: string;
	minimumStrength: number;
	strength: number;
	submitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const enterPasswordInitialState: EnterPasswordState = {
	email: '',
	minimumStrength: 0,
	strength: 0,
	submitDisabled: false,
	violations: {}
};

export interface IndexState {
	displayType: DisplayType;
	message: string;
	messageType: MessageType;
	phoneVerificationRestartRequested: boolean;
	phoneVerificationWasStopped: boolean;
}

const indexInitialState: IndexState = {
	displayType: DisplayType.DISPLAY_TYPE_NONE,
	message: '',
	messageType: MessageType.INFO,
	phoneVerificationRestartRequested: false,
	phoneVerificationWasStopped: false
};

export interface PhoneVerificationCodeState {
	maximumDurationInSeconds: number | null;
	submitDisabled: boolean;
	verificationStopped: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const phoneVerificationCodeInitialState: PhoneVerificationCodeState = {
	maximumDurationInSeconds: null,
	submitDisabled: false,
	verificationStopped: false,
	violations: {}
};

export interface RetryPhoneVerificationStartState {
	buttonText: string;
	submitDisabled: boolean;
}

const retryPhoneVerificationStartInitialState: RetryPhoneVerificationStartState = {
	buttonText: 'Probeer opnieuw',
	submitDisabled: false
};

export interface ConfirmEmployeeRegistrationState {
	enterPassword: EnterPasswordState;
	index: IndexState;
	phoneVerificationCode: PhoneVerificationCodeState;
	retryPhoneVerificationStart: RetryPhoneVerificationStartState;
}

const enterPasswordReducer = (
	state: EnterPasswordState = enterPasswordInitialState,
	action: ActionTypes
): EnterPasswordState => {
	const violations = {...state.violations};
	let strength = state.strength;
	let submitDisabled = state.submitDisabled;
	switch (action.type) {
		case getType(confirmEmployeeRegistrationSceneActivated):
			return enterPasswordInitialState;
		case getType(confirmEmployeeRegistrationSceneStateRetrieved):
		case getType(confirmEmployeeRegistrationSceneStateChanged):

			if (action.payload.state.insufficientEvaluatedPasswordStrength) {
				violations.password = [{message: 'Het nieuwe wachtwoord is niet sterk (veilig) genoeg'}];
				strength = action.payload.state.insufficientEvaluatedPasswordStrength.strength;
				submitDisabled = false;
			}

			return {
				...state,
				email: action.payload.state.emailAddress,
				minimumStrength: action.payload.state.requestedPasswordMinimumStrength ?
					action.payload.state.requestedPasswordMinimumStrength.strength :
					0,
				strength,
				submitDisabled,
				violations
			};
		case getType(confirmEmployeeRegistrationScenePasswordInputChanged):
			return {...state, violations: {}};
		case getType(confirmEmployeeRegistrationScenePasswordStrengthEvaluated):
			return {...state, strength: action.payload.passwordStrength.strength};
		case getType(confirmEmployeeRegistrationScenePasswordSubmitted):
			return {...state, submitDisabled: true};
		case getType(confirmEmployeeRegistrationSceneEnterPasswordValidationException):
			return {...state, submitDisabled: false, violations: action.payload.violations};
		default:
			return state;
	}
};

const indexReducer = (
	state: IndexState = indexInitialState,
	action: ActionTypes
): IndexState => {
	let remainingSeconds: number | undefined;
	let phoneVerificationActive: boolean | undefined | number;

	switch (action.type) {
		case getType(confirmEmployeeRegistrationSceneActivated):
			return indexInitialState;
		case getType(confirmEmployeeRegistrationSceneStateRetrieved):
		case getType(confirmEmployeeRegistrationSceneStateChanged):
			if (action.payload.state.emailRegistrationExpired || action.payload.state.registrationExpired)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_NONE,
					message: 'De link om je registratie te bevestigen is inmiddels verlopen. Neem contact op met de systeembeheerder',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.phoneBlocked)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_NONE,
					message: 'Uw telefoon is geblokkeerd, neem contact met ons op',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.failedToSendPhoneVerificationCode)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START,
					message: 'Er is een technisch probleem met het verificatie proces. ' +
						'Wij kunnen op dit moment geen code sturen naar uw telefoon',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.phoneUnblocked)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message: 'Voordat de registratie compleet gemaakt kan worden, willen we graag het ' +
						'telefoonnummer verifiëren',
					messageType: MessageType.INFO
				};

			if (action.payload.state.phoneVerificationStoppedWithReason)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message: '',
					messageType: MessageType.INFO,
					phoneVerificationWasStopped: true
				};

			remainingSeconds = action.payload.state.phoneVerificationTimeRemainingInSeconds;
			phoneVerificationActive = remainingSeconds && Number.isInteger(remainingSeconds) && remainingSeconds > 0;

			if (phoneVerificationActive) {
				let message = 'We hebben een verificatiecode verzonden naar uw telefoon';
				let messageType = MessageType.INFO;
				if (state.phoneVerificationWasStopped)
					message = 'We hebben een nieuwe code verzonden naar uw telefoon. De vorige code is niet meer geldig';

				if (action.payload.state.failedToProcessPhoneVerificationCode) {
					message = 'Er is een technische fout opgetreden tijdens het verifiëren van uw code. ' +
						'De code is nog steeds geldig. U kunt het opnieuw proberen';
					messageType = MessageType.ERROR;
				}

				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message,
					messageType
				};
			}

			if (action.payload.state.requestedPasswordMinimumStrength)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_ENTER_PASSWORD,
					message: 'Kies een wachtwoord voor uw nieuwe account',
					messageType: MessageType.INFO
				};

			if (action.payload.state.accountCreationFailedUnexpectedly)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_NONE,
					message: 'Er is een onverwachte fout opgetreden bij het aanmaken van uw account. Neem contact op met de systeembeheerder',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.registrationComplete)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_COMPLETION_MESSAGE,
					message: '',
					messageType: MessageType.INFO
				};

			return state;

		case getType(confirmEmployeeRegistrationSceneRequestRestartPhoneVerification):
			return {...state, phoneVerificationRestartRequested: true};
		default:
			return state;
	}
};

const phoneVerificationCodeReducer = (
	state: PhoneVerificationCodeState = phoneVerificationCodeInitialState,
	action: ActionTypes
): PhoneVerificationCodeState => {
	let verificationStopped: boolean;
	let remainingSeconds: number | undefined;
	let phoneVerificationActive: boolean | number | undefined;

	// noinspection DuplicatedCode
	switch (action.type) {
		case getType(confirmEmployeeRegistrationSceneActivated):
			return phoneVerificationCodeInitialState;
		case getType(confirmEmployeeRegistrationSceneStateRetrieved):
		case getType(confirmEmployeeRegistrationSceneStateChanged):
			verificationStopped = !!action.payload.state.phoneVerificationStoppedWithReason;
			if (verificationStopped || action.payload.state.phoneUnblocked) {
				const violations = {...state.violations};
				let violationMessage;

				switch (action.payload.state.phoneVerificationStoppedWithReason) {
					case 'INVALID_VERIFICATION_CODE':
						violationMessage = 'De ingevoerde verificatiecode was niet geldig';
						break;
					case 'VERIFICATION_TIMEOUT':
						violationMessage = 'Uw verificatiecode is verlopen';
						break;
					default:
						violationMessage = null;
				}

				if (violationMessage)
					violations.verificationCode = [{message: violationMessage}];

				return {
					...state,
					maximumDurationInSeconds: null,
					submitDisabled: action.payload.state.expectingPhoneVerificationRestart,
					verificationStopped: true,
					violations
				};
			}

			remainingSeconds = action.payload.state.phoneVerificationTimeRemainingInSeconds;
			phoneVerificationActive = remainingSeconds && Number.isInteger(remainingSeconds) && remainingSeconds > 0;

			if (phoneVerificationActive)
				return {
					...state,
					maximumDurationInSeconds: remainingSeconds || null,
					submitDisabled: false,
					verificationStopped: false,
					violations: {}
				};

			return state;
		case getType(confirmEmployeeRegistrationSceneRequestRetryPhoneVerification):
		case getType(confirmEmployeeRegistrationSceneRequestRestartPhoneVerification):
		case getType(confirmEmployeeRegistrationScenePhoneVerificationCodeSubmitted):
			return {...state, submitDisabled: true};
		case getType(confirmEmployeeRegistrationSceneRetryPhoneVerificationUnexpectedError):
		case getType(confirmEmployeeRegistrationSceneRestartPhoneVerificationUnexpectedError):
		case getType(confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeUnexpectedError):
			return {...state, submitDisabled: false};
		case getType(confirmEmployeeRegistrationSceneEnterPhoneVerificationCodeValidationException):
			return {...state, violations: action.payload.violations, submitDisabled: false};
		case getType(confirmEmployeeRegistrationScenePhoneVerificationCodeInputChanged):
			return {...state, violations: {}};
		default:
			return state;
	}
};

// noinspection DuplicatedCode
const retryPhoneVerificationStartReducer = (
	state: RetryPhoneVerificationStartState = retryPhoneVerificationStartInitialState,
	action: ActionTypes
): RetryPhoneVerificationStartState => {
	switch (action.type) {
		case getType(confirmEmployeeRegistrationSceneActivated):
			return retryPhoneVerificationStartInitialState;
		case getType(confirmEmployeeRegistrationSceneStateRetrieved):
		case getType(confirmEmployeeRegistrationSceneStateChanged):
		case getType(confirmEmployeeRegistrationSceneRetryPhoneVerificationUnexpectedError):
			return {...state, submitDisabled: false};
		case getType(confirmEmployeeRegistrationSceneRequestRetryPhoneVerification):
			return {...state, submitDisabled: true};
		default:
			return state;
	}
};

export default combineReducers({
	enterPassword: enterPasswordReducer,
	index: indexReducer,
	phoneVerificationCode: phoneVerificationCodeReducer,
	retryPhoneVerificationStart: retryPhoneVerificationStartReducer
});
